import { Instagram, MapPin, MessageCircle, Clock } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useSeo, useSettings } from "@/lib/shop";
import { waContacts } from "@/lib/format";

const Page = ({ title, children, testid }) => (
  <div className="container-x py-10 max-w-4xl" data-testid={testid}>
    <h1 className="font-heading text-3xl sm:text-4xl font-extrabold">{title}</h1>
    <div className="h-1.5 w-20 bg-[var(--bk-yellow)] mt-3 mb-8 rounded-full" />
    {children}
  </div>
);

export function FaqBlock() {
  const s = useSettings();
  if (!s.faq?.length) return null;
  return (
    <div data-testid="faq-block">
      <h2 className="font-heading text-2xl font-extrabold mb-3">FAQ</h2>
      <Accordion type="single" collapsible className="rounded-xl border bg-white px-4">
        {s.faq.map((f, i) => (
          <AccordionItem key={i} value={`f${i}`}>
            <AccordionTrigger data-testid={`faq-item-${i}`} className="text-left font-semibold">{f.q}</AccordionTrigger>
            <AccordionContent className="text-neutral-700 whitespace-pre-line">{f.a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}

export function PoTermsBlock() {
  const t = useSettings().po_terms || {};
  const rows = [["Ketentuan DP", t.dp_policy], ["Jadwal pelunasan", t.settlement], ["Pembatalan", t.cancellation], ["Pengembalian dana", t.refund], ["Keterlambatan supplier", t.late_supplier]].filter((r) => r[1]);
  return (
    <div className="rounded-2xl border-2 border-neutral-900 bg-white p-5 sm:p-6" data-testid="po-terms-block">
      <h2 className="font-heading text-2xl font-extrabold mb-4">Ketentuan Pre-Order</h2>
      <dl className="space-y-4">
        {rows.map(([k, v]) => (
          <div key={k}><dt className="font-bold text-sm">{k}</dt><dd className="text-sm text-neutral-700 whitespace-pre-line mt-0.5">{v}</dd></div>
        ))}
      </dl>
    </div>
  );
}

export function Guide() {
  useSeo("Panduan Pemesanan", "Cara memesan produk Ready Stock dan Pre-Order di Bengkel Toys & Hobbies.");
  const s = useSettings();
  return (
    <Page title="Panduan Pemesanan" testid="guide-page">
      <p className="prose-text">{s.guide}</p>
      <div className="mt-10 grid gap-8"><PoTermsBlock /><FaqBlock /></div>
    </Page>
  );
}

export function About() {
  useSeo("Tentang Kami", "Tentang Bengkel Toys & Hobbies, toko mainan dan hobi di Pontianak.");
  const s = useSettings();
  return (
    <Page title="Tentang Kami" testid="about-page">
      <div className="grid md:grid-cols-[1fr_260px] gap-8 items-start">
        <p className="prose-text">{s.about}</p>
        <div className="rounded-3xl bg-[var(--bk-yellow)] halftone border-2 border-neutral-900 p-6">
          <img src="/brand/logo.webp" alt="Bengkel Toys & Hobbies" className="w-full h-auto" loading="lazy" />
          <p className="font-display text-center mt-3">{s.tagline}</p>
        </div>
      </div>
    </Page>
  );
}

export function Contact() {
  useSeo("Kontak dan Lokasi", "Alamat, WhatsApp, Instagram, dan jam operasional Bengkel Toys & Hobbies Pontianak.");
  const s = useSettings();
  const card = "rounded-2xl border-2 border-neutral-900 bg-white p-5 flex gap-3";
  return (
    <Page title="Kontak dan Lokasi" testid="contact-page">
      <div className="grid sm:grid-cols-2 gap-4">
        <div className={card}><MapPin className="h-6 w-6 text-[var(--bk-orange)] shrink-0" /><div><b>Alamat</b><p className="text-sm mt-1">{s.address}</p>{s.maps_url && <a href={s.maps_url} target="_blank" rel="noreferrer" className="text-sm font-semibold underline mt-2 inline-block" data-testid="contact-maps-link">Buka Google Maps</a>}</div></div>
        <div className={card}><Clock className="h-6 w-6 text-[var(--bk-orange)] shrink-0" /><div><b>Jam Operasional</b>{(s.hours || []).map((h, i) => <p key={i} className="text-sm mt-1">{h.label}: {h.value}</p>)}</div></div>
        {waContacts(s).map((c, i) => (
          <a key={c.number} href={`https://wa.me/${c.number}`} target="_blank" rel="noreferrer" className={`${card} hover:bg-emerald-50`} data-testid={`contact-wa-link-${i}`}><MessageCircle className="h-6 w-6 text-emerald-600 shrink-0" /><div><b>WhatsApp {c.label}</b><p className="text-sm mt-1">{c.display}</p></div></a>
        ))}
        <a href={`https://instagram.com/${s.instagram}`} target="_blank" rel="noreferrer" className={`${card} hover:bg-pink-50`} data-testid="contact-ig-link"><Instagram className="h-6 w-6 text-pink-600 shrink-0" /><div><b>Instagram</b><p className="text-sm mt-1">@{s.instagram}</p></div></a>
      </div>
      {s.maps_embed && <iframe title="Peta lokasi" src={s.maps_embed} loading="lazy" className="mt-6 w-full h-80 rounded-2xl border-2 border-neutral-900" referrerPolicy="no-referrer-when-downgrade" />}
    </Page>
  );
}

export function Policy({ k, title }) {
  useSeo(title);
  const s = useSettings();
  return (
    <Page title={title} testid={`policy-page-${k}`}>
      <p className="prose-text">{s[k] || "Belum diatur."}</p>
    </Page>
  );
}
